<template>
  <div
    ref="container"
    class="game-offer-accounts container">
    <game-accounts-header>
      <div class="game-offer-accounts__filters">
        <accounts-filters-row
          v-if="filterFieldsWithValues"
          :key="`filter_${product.id}-${$route.query.preset_id}`"
          class="game-offer-accounts__pc-filters"
          :currency-settings="filterFieldsWithValues"
          currency-field-type="component_view"
          @input="handleFiltersInput"
          @search="handleSearchInput"
          @price-range="handlePriceRange" />

        <accounts-mobile-filters-row
          v-if="filterFieldsWithValues"
          :key="`filter-mobile_${product.id}-${$route.query.preset_id}`"
          class="game-offer-accounts__mobile-filters"
          @search="handleSearchInput"
          @on-filters-open="handleFiltersOpen" />
      </div>
    </game-accounts-header>

    <div class="game-offer-accounts__count">
      <span>{{ accountOffers.total }} offer{{ accountOffers.total > 1 ? 's' : '' }} available</span>

      <product-sort
        :active-option="activeSortOptionId"
        :sort-options="sortOptions"
        @sortOption="changeCurrentSort" />
    </div>

    <div
      v-if="accountsPresets && headerPresets.length"
      class="game-offer-accounts__presets game-offer-accounts__presets--header">
      <router-link
        v-for="(preset) in headerPresets"
        :key="preset.id"
        :to="{ query: { preset_id: preset.id } }"
        :class="{ 'game-offer-accounts__preset--active': visuallyActivePresetId === preset.id }"
        class="button button-round-bordered"
        @click.native="handlePresetClick">
        {{ preset.title }}
      </router-link>
    </div>

    <div class="game-offer-accounts__offers">
      <account-card
        v-for="(account, index) in accountOffers.data"
        :key="index"
        :account="account" />
    </div>

    <ui-pagination
      v-if="accountOffers.total > itemsPerPage"
      v-model="params.page"
      :total="paginationTotal"
      :items-per-page="itemsPerPage"
      class="game-offer-accounts__pagination"
      @paginate="scrollToTop()" />

    <div
      v-if="footerPresets.length"
      class="game-offer-accounts__presets game-offer-accounts__presets--footer">
      <router-link
        v-for="(preset) in footerPresets"
        :key="preset.id"
        :to="{ query: { preset_id: preset.id } }"
        :class="{ 'game-offer-accounts__preset--active': visuallyActivePresetId === preset.id }"
        class="button button-round-bordered"
        @click.native="handlePresetClick(true)">
        {{ preset.title }}
      </router-link>
    </div>
  </div>
</template>

<script>
import AccountsFiltersRow from 'Components/Accounts/AccountsFiltersRow.vue';
import AccountCard from 'Components/Accounts/AccountCard.vue';
import ProductSort from 'Root/pages/game/components/ProductSort.vue';
import SortModel from 'Models/SortModel';
import UiPagination from 'Components/ui/Pagination/UiPagination.vue';
import { scrollTo, _debounce as debounce } from 'Root/helpers/tools';
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import axios from 'axios';
import { extractIdFromSlug } from 'Root/helpers/product';
import CurrencyOfferModel from 'Models/CurrencyOfferModel';
import CurrencyFilterModel from 'Models/CurrencyFilterModel';
import { transformFilterEntryIntoMeilisearchParams } from 'Root/helpers/accounts';
import AccountsMobileFiltersRow from 'Components/Accounts/Mobile/AccountsMobileFiltersRow.vue';
import AccountsFiltersPopup from 'Components/Popups/AccountsFiltersPopup.vue';
import GameAccountsHeader from 'Root/pages/game/components/GameAccountsHeader.vue';

const LIMIT = 24;
const MAX_OFFERS = 2000;

let accountOffersAbortController = null;
let accountPresetsAbortController = null;

function convertFilterValues(filters) {
  const convertedFilters = {};

  Object.keys(filters).forEach((filterKey) => {
    convertedFilters[filterKey] = {};

    Object.keys(filters[filterKey]).forEach((key) => {
      convertedFilters[filterKey][key] = filters[filterKey][key].map((value) => {
        const numberValue = Number(value);
        return Number.isNaN(numberValue) ? value : numberValue;
      });
    });
  });

  return convertedFilters;
}

export default {
  name: 'GameOfferAccounts',
  components: {
    GameAccountsHeader,
    AccountsMobileFiltersRow,
    UiPagination,
    ProductSort,
    AccountCard,
    AccountsFiltersRow,
  },

  async asyncData({ context, store, router }) {
    const { CancelToken } = axios;
    const accountOffersAbortControllerAsync = CancelToken.source();

    const fullSlug = router.currentRoute.params.id;
    const productId = router.currentRoute.meta?.tagAsOfferInfo?.offerId || extractIdFromSlug(fullSlug);
    const currencySettings = context.getCurrencySettings()[productId];
    const route = router.currentRoute;
    const { query } = route;

    // этот компонент динамически подгружается в зависимости от условия в GameOffer.vue в offerComponent
    // asyncData вызывается, если указанный компонент импортирован в GameOffer.vue
    // это вызывает баг, который вызывает asyncData компонента и даже не должен быть отрисован
    // поэтому делаем проверку на наличие currencySettings и тип маркетплейса
    if (currencySettings?.marketplace_type !== CurrencyOfferModel.TYPES.ACCOUNT) {
      return null;
    }

    await store.dispatch('products/getAccountsPresetsAction', {
      currencySettingId: currencySettings.id,
      cancelToken: accountOffersAbortControllerAsync.token,
    });

    let params = {
      product_currency_setting_id: currencySettings.id,
      hit_per_page: LIMIT,
      page: +query.page || 1,
      price_range: query?.price_range?.split(',')?.map((el) => Number(el)) ?? null,
      filters: {},
      q: query.q || '',
    };

    if (query.preset_id) {
      const preset = store.state.products.accountsPresets?.find((item) => item.id === +query.preset_id);

      if (preset?.params) {
        params = {
          ...params,
          ...preset.params,
          preset_id: +query.preset_id,
        };
      }
    } else {
      if (query.sort_column) {
        params.sort_column = query.sort_column;
      }

      if (query.sort_direction) {
        params.sort_direction = query.sort_direction;
      }

      Object.keys(query).forEach((key) => {
        if (key.includes('.')) {
          const [filterKey, filterType] = key.split('.');
          if (!params.filters[filterKey]) {
            params.filters[filterKey] = {};
          }
          params.filters[filterKey][filterType] = query[key].split(',');
        }
      });

      params.filters = convertFilterValues(params.filters);
    }

    return store.dispatch('products/getAccountOffersAction', {
      params,
      cancelToken: accountOffersAbortControllerAsync.token,
    });
  },

  data() {
    return {
      activeSortOptionId: SortModel.OPTION_NEWEST,
      sortOptions: [
        {
          id: SortModel.OPTION_NEWEST,
          title: 'Newest first',
          sort_column: 'created_at',
          sort_direction: 'desc',
        },
        {
          id: SortModel.OPTION_OLDEST,
          title: 'Oldest first',
          sort_column: 'created_at',
          sort_direction: 'asc',
        },
        {
          id: SortModel.OPTION_PRICE_UP,
          title: 'Price: Low to High',
          sort_column: 'price_per_step',
          sort_direction: 'asc',
        },
        {
          id: SortModel.OPTION_PRICE_DOWN,
          title: 'Price: High to Low',
          sort_column: 'price_per_step',
          sort_direction: 'desc',
        },
      ],

      /* Filters and Sorting */
      params: {
        hit_per_page: LIMIT,
        page: +this.$route.query?.page || 1,
        filters: {},
        q: this.$route.query?.q || '',
        price_range: this.priceRange,
      },
      itemsPerPage: LIMIT,

      isMobileFilterOpened: false,
      activePresetId: +this.$route.query?.preset_id || null,
      visuallyActivePresetId: +this.$route.query?.preset_id || null,
    };
  },

  computed: {
    ...mapState('products', ['product', 'tags', 'accountOffers', 'accountsPresets']),
    ...mapGetters('products', ['filterFields']),

    activeSortOption() {
      return this.sortOptions.find((option) => option.id === this.activeSortOptionId);
    },

    filterFieldsWithValues() {
      const filterFieldsWithValues = { ...this.filterFields };

      // eslint-disable-next-line
      filterFieldsWithValues.product_currency_setting_fields = filterFieldsWithValues.product_currency_setting_fields.map((field) => {
        const fieldId = field.id;
        const filter = this.accountOffers.params?.filters?.[fieldId];

        field.default_value = null;
        field.default_range_value = null;

        if (filter) {
          if (field.component_edit === CurrencyFilterModel.ID_BY_FIELD.MULTIPLE_GRID_SELECT) {
            field.default_value = field.product_currency_setting_field_values
              .filter((item) => filter.values?.includes(item.value));

            if (field.has_values_counter) {
              field.default_range_value = filter.range;
            }
          } else if (field.has_custom_value) {
            if (field.component_view === CurrencyFilterModel.ID_BY_FIELD.MULTIPLE_SEARCH_INPUT) {
              field.default_value = field.component_view_data?.values
                .filter((value) => value.min === filter?.custom_range?.[0]
                  && value.max === filter?.custom_range?.[1]);
            } else if (field.component_view === CurrencyFilterModel.ID_BY_FIELD.DROPDOWN_RANGE) {
              field.default_value = filter.custom_range;
            }
          } else {
            field.default_value = field.product_currency_setting_field_values
              .filter((item) => filter.values?.includes(item.value));
          }
        }

        return field;
      });

      return filterFieldsWithValues;
    },

    sortedPresets() {
      return this.accountsPresets
        ? [...this.accountsPresets]
          .sort((a, b) => (a.priority === b.priority ? a.id - b.id : b.priority - a.priority))
        : [];
    },
    headerPresets() {
      return this.sortedPresets.filter((el) => el.show_in_header);
    },

    footerPresets() {
      return this.sortedPresets.filter((el) => !el.show_in_header);
    },

    activePreset() {
      return this.accountsPresets?.find((preset) => preset.id === +this.$route.query?.preset_id);
    },

    priceRange() {
      return this.$route.query?.price_range?.split(',')?.map((el) => Number(el)) ?? null;
    },

    paginationTotal() {
      if (this.accountOffers.total > MAX_OFFERS) {
        return MAX_OFFERS;
      }

      return this.accountOffers.total;
    },
  },

  watch: {
    params: {
      handler() {
        if (
          this.activePreset
          && JSON.stringify(this.activePreset?.params?.filters) !== JSON.stringify(this.params.filters)
        ) {
          this.activePresetId = null;
          this.$router.push({ query: {} });
        }

        if (!this.activePreset) {
          this.updatePageQuery();
        }

        this.getAccountOffers();
      },
      deep: true,
    },

    // SKY-6129: clear params while moving to another account catalog
    '$route.path': {
      async handler() {
        this.resetAllFilters();
      },
    },

    '$route.query': {
      async handler(query) {
        if (query.preset_id && +query.preset_id !== this.visuallyActivePresetId) {
          if (this.activePreset?.params) {
            this.params = {
              ...JSON.parse(JSON.stringify(this.activePreset.params)),
              price_range: this.priceRange,
              preset_id: +query.preset_id,
              page: 1,
              hit_per_page: LIMIT,
            };

            this.mutateAccountOffers({
              offers: this.accountOffers.data,
              total: this.accountOffers.total,
              params: this.params,
            });
          }
        }
      },
    },

    // SKY-6194: update filter presets on product change
    product() {
      this.getAccountPresets();
    },
  },

  async created() {
    if (this.accountOffers.total === null && !this.activePreset?.params) {
      await this.getAccountOffers();
    }

    if (this.accountsPresets === null) {
      await this.getAccountPresets();
    }

    if (this.activePreset?.params) {
      this.params = {
        ...this.params,
        ...JSON.parse(JSON.stringify(this.activePreset.params)),
        preset_id: +this.activePreset?.id,
      };
    }

    this.$emit('componentReady');
  },

  beforeDestroy() {
    this.mutateAccountOffers({ offers: [], total: null, params: {} });
    this.mutateAccountsPresets(null);
  },

  methods: {
    ...mapMutations('products', ['mutateAccountOffers', 'mutateAccountsPresets']),
    ...mapActions('products', ['getAccountOffersAction', 'getAccountsPresetsAction']),

    // eslint-disable-next-line func-names
    getAccountOffers: debounce(function () {
      // SKY-6360: fix for the case of switching between gold and account offers
      if (
        !this.product?.product_currency_setting_id
        || this.product?.product_currency_setting?.marketplace_type !== CurrencyOfferModel.TYPES.ACCOUNT
      ) {
        return;
      }

      if (accountOffersAbortController !== null) {
        accountOffersAbortController.cancel();
      }

      const { CancelToken } = this.$axios;
      accountOffersAbortController = CancelToken.source();

      this.getAccountOffersAction({
        id: this.product.product_currency_setting_id,
        params: {
          ...this.params,
          ...(this.activePreset?.params ? JSON.parse(JSON.stringify(this.activePreset.params)) : {}),
          product_currency_setting_id: this.product?.product_currency_setting_id,
        },
        cancelToken: accountOffersAbortController.token,
      });
    }, 500),

    changeCurrentSort(sortOptionId) {
      this.activeSortOptionId = sortOptionId;
      this.$set(this.params, 'sort_column', this.activeSortOption.sort_column);
      this.$set(this.params, 'sort_direction', this.activeSortOption.sort_direction);
    },

    scrollToTop() {
      const y = this.$refs.container.offsetTop || 0;
      scrollTo(0, y);
    },

    handleFiltersInput(entry) {
      const { fieldId, key, value } = transformFilterEntryIntoMeilisearchParams(entry);
      if (value.length) {
        if (this.params.filters[fieldId]) {
          const field = this.filterFields.product_currency_setting_fields.find((item) => item.id === fieldId);
          // check if field is range and clear its value if values are equal to default
          if (field.has_values_counter) {
            if (
              value[0] === 1
              && value[1] === field.product_currency_setting_field_values.length
            ) {
              this.$delete(this.params.filters, fieldId);
              return;
            }
          }

          // same for custom range
          if (field.has_custom_value && field.component_view === CurrencyFilterModel.ID_BY_FIELD.DROPDOWN_RANGE) {
            if (value[0] === field.component_view_data.values[0].min
              && value[1] === field.component_view_data.values[field.component_view_data.values.length - 1].max) {
              this.$delete(this.params.filters, fieldId);
              return;
            }
          }

          this.$set(this.params.filters[fieldId], key, value);
        } else {
          this.$set(this.params.filters, fieldId, { [key]: value });
        }
      } else {
        this.$delete(this.params.filters, fieldId);
      }

      this.$set(this.params, 'page', 1);
    },

    updatePageQuery() {
      const searchParams = new URLSearchParams();

      if (this.params.page !== 1) {
        searchParams.set('page', this.params.page);
      }

      if (this.params.sort_column) {
        searchParams.set('sort_column', this.params.sort_column);
      }

      if (this.params.sort_direction) {
        searchParams.set('sort_direction', this.params.sort_direction);
      }

      if (this.params.q) {
        searchParams.set('q', this.params.q);
      }

      if (this.params.price_range && this.params.price_range.join() !== '0,2500') {
        searchParams.set('price_range', this.params.price_range);
      }

      if (this.visuallyActivePresetId) {
        searchParams.set('preset_id', this.visuallyActivePresetId);
      }

      Object.keys(this.params.filters).forEach((filterKey) => {
        const filter = this.params.filters[filterKey];
        // eslint-disable-next-line no-shadow
        Object.keys(filter).forEach((key) => {
          searchParams.append(`${filterKey}.${key}`, filter[key]);
        });
      });

      if (
        typeof window !== 'undefined'
        && JSON.stringify(this.$route.query) !== JSON.stringify(Object.fromEntries(searchParams))
      ) {
        this.$router.push({
          name: this.$route.name,
          query: Object.fromEntries(searchParams),
        });
      }
    },

    handleSearchInput(searchValue) {
      if (searchValue) {
        this.$set(this.params, 'q', searchValue);
      } else {
        this.$delete(this.params, 'q');
      }

      this.$set(this.params, 'page', 1);
    },

    handlePriceRange(values) {
      if (values) {
        this.$set(this.params, 'price_range', values);
      } else {
        this.$delete(this.params, 'price_range');
      }

      this.$set(this.params, 'page', 1);
    },

    async getAccountPresets() {
      if (accountPresetsAbortController !== null) {
        accountPresetsAbortController.cancel();
      }

      const { CancelToken } = this.$axios;
      accountPresetsAbortController = CancelToken.source();

      await this.getAccountsPresetsAction({
        currencySettingId: this.product.product_currency_setting_id,
        cancelToken: accountPresetsAbortController.token,
      });
    },

    handlePresetClick(withScrollToTop) {
      if (this.activePresetId === this.activePreset?.id) {
        this.$router.push({ query: { preset_id: null } });

        this.params = {
          filters: {},
          q: '',
          price_range: this.priceRange,
          page: 1,
          hit_per_page: LIMIT,
        };

        this.mutateAccountOffers({
          offers: this.accountOffers.data,
          total: this.accountOffers.total,
          params: this.params,
        });

        this.activePresetId = null;
        this.visuallyActivePresetId = null;

        return;
      }

      this.activePresetId = this.activePreset?.id;
      this.visuallyActivePresetId = this.activePreset?.id;

      if (withScrollToTop) {
        this.scrollToTop();
      }
    },

    handleFiltersOpen() {
      this.$vuedals.openMobilePanel({
        class: 'accounts-filters-popup',
        component: AccountsFiltersPopup,
        closeOnBackdrop: true,
        data: {
          currencySettings: this.filterFieldsWithValues,
        },
        onClose: async ({ name, ...emitData }) => {
          if (emitData?.shouldClear) {
            this.clearFilters();
            return;
          }

          const entries = emitData?.entries || [];
          entries.forEach((entry) => {
            this.handleFiltersInput(entry);
          });

          const priceRange = emitData?.priceRange ?? null;
          this.handlePriceRange(priceRange);
        },
      });
    },

    clearFilters() {
      this.params = {
        filters: {},
        q: '',
        price_range: null,
        page: 1,
        hit_per_page: LIMIT,
      };
    },

    resetAllFilters() {
      this.clearFilters();

      this.activeSortOptionId = SortModel.OPTION_NEWEST;

      this.activePresetId = null;
      this.visuallyActivePresetId = null;

      this.mutateAccountOffers({
        offers: this.accountOffers.data,
        total: this.accountOffers.total,
        params: { filters: {} },
      });

      this.$nextTick(() => {
        if (Object.keys(this.params.filters).length > 0) {
          this.params.filters = {};
        }
      });
    },
  },
};
</script>

<style lang="scss">
.offer-bg-img.background--gradient::after {
  bottom: 0;
  height: 300px;
}
</style>

<style scoped lang="scss">
.game-offer-accounts {
  position: relative;
  padding-top: 0;
  transition: 0.3s;
}

.game-offer-accounts__offers {
  display: grid;
  grid-template-columns: repeat(4, calc(25% - 12px));
  grid-gap: 16px;
}

.game-offer-accounts__count {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 27px 0 16px;
  font-size: 15px;
  line-height: 18px;
  color: $color--basic-white;
}

.game-offer-accounts__pagination {
  margin: 40px 0;
}

.game-offer-accounts__show-filters {
  display: none;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: 24px;
  font-size: 13px;
  padding: 12px;
  background: linear-gradient(90deg, $color--basic-purple, $color--basic-blue);
}

.game-offer-accounts__show-filters-arrow {
  width: 16px;
  height: 16px;
  margin-left: 8px;
}

.game-offer-accounts__show-more-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  font-size: 13px;
  font-weight: 500;
  color: $color--basic-white;
}

.game-offer-accounts__presets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  &--header {
    margin-bottom: 32px;
  }

  &--footer {
    margin-top: 32px;
  }
}

.game-offer-accounts__preset--active {
  background-color: #{$color--basic-dark-gray};
}

.game-offer-accounts__mobile-filters {
  display: none;
}

@media screen and (max-width: 1600px) {
  .game-offer-accounts__offers {
    grid-template-columns: repeat(3, calc(33.3333% - 10.667px));
  }
}

@media screen and #{$screen-less-than-1280} {
  .game-offer-accounts__offers {
    grid-template-columns: repeat(2, calc(50% - 8px));
  }
}

@media screen and #{$screen-less-than-1024} {
  .game-offer-accounts__show-more-button {
    display: none;
  }

  .game-offer-accounts__presets--header {
    flex-wrap: nowrap;
    overflow: auto;
    padding-bottom: 8px;
    scrollbar-width: thin;
  }
}

@media screen and #{$screen-less-than-640} {
  .game-offer-accounts__offers {
    grid-template-columns: 1fr;
  }

  .game-offer-accounts__show-filters {
    display: flex;
  }

  .game-offer-accounts__pc-filters {
    display: none;
  }

  .game-offer-accounts__mobile-filters {
      display: flex;
  }

  .game-offer-accounts__filters {
    position: sticky;
    z-index: 3;
    top: 0;
    padding: 24px 0;
    background: linear-gradient(
        180deg,
        $color--basic-black 0%,
        $color--basic-black 65%,
        #{rgba($color--basic-black, 0.7)} 80%,
        transparent 100%
    );
  }

  .game-offer-accounts__presets {
    margin-bottom: 16px;
  }
}
</style>
